/**
 * Column Operation Handlers
 * Extracted from projectDataUtils.ts applyOperation
 * Handles Kanban column operations
 */

import type { ProjectData, NodeOperation } from '@/shared/types/projectData';
import { cloneProjectData } from '@/shared/utils/projectDataUtils';
import {
  normalizeKanbanColumn,
  normalizeKanbanColumnId,
} from '@/shared/utils/kanbanState';

function cloneProjectDataWithColumns(data: ProjectData): ProjectData {
  const updated = cloneProjectData(data);
  updated.kanbanColumns = Array.isArray(updated.kanbanColumns)
    ? updated.kanbanColumns.map((column) => ({ ...column }))
    : [];
  return updated;
}

export function handleAddColumn(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.columnData) return data;
  const updated = cloneProjectDataWithColumns(data);
  if (!updated.kanbanColumns) {
    updated.kanbanColumns = [];
  }
  const normalizedColumn = normalizeKanbanColumn(op.columnData);
  const exists = updated.kanbanColumns.find((column) => normalizeKanbanColumnId(column.id) === normalizedColumn.id);
  if (!exists) {
    updated.kanbanColumns = [...updated.kanbanColumns, normalizedColumn];
  }
  return updated;
}

export function handleUpdateColumn(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.columnId || !op.columnData) return data;
  const updated = cloneProjectDataWithColumns(data);
  if (!updated.kanbanColumns) {
    updated.kanbanColumns = [];
  }
  const normalizedColumnId = normalizeKanbanColumnId(op.columnId);
  const normalizedColumn = normalizeKanbanColumn(op.columnData);
  const index = updated.kanbanColumns.findIndex((column) => normalizeKanbanColumnId(column.id) === normalizedColumnId);
  if (index !== -1) {
    updated.kanbanColumns = [...updated.kanbanColumns];
    updated.kanbanColumns[index] = {
      ...updated.kanbanColumns[index],
      ...normalizedColumn,
    };
  } else {
    updated.kanbanColumns = [...updated.kanbanColumns, normalizedColumn];
  }
  return updated;
}

export function handleDeleteColumn(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.columnId) return data;
  const updated = cloneProjectDataWithColumns(data);
  if (updated.kanbanColumns) {
    const normalizedColumnId = normalizeKanbanColumnId(op.columnId);
    updated.kanbanColumns = updated.kanbanColumns.filter((column) => normalizeKanbanColumnId(column.id) !== normalizedColumnId);
  }
  return updated;
}

export function handleReorderColumns(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.columnOrder || !Array.isArray(op.columnOrder)) return data;
  const updated = cloneProjectDataWithColumns(data);
  const existing = (updated.kanbanColumns || []).map(normalizeKanbanColumn);
  const requested = [...new Set(op.columnOrder.map(normalizeKanbanColumnId))];
  const ids = [...requested, ...existing.map(column => column.id).filter(id => !requested.includes(id))];
  updated.kanbanColumns = ids.filter(id => existing.some(column => column.id === id)).map((id, index) => {
    const column = existing.find(column => column.id === id)!;
    return { ...column, order: index,
      aggregateOrder: op.aggregateOrder && Object.prototype.hasOwnProperty.call(op.aggregateOrder, id) ? op.aggregateOrder[id] : column.aggregateOrder };
  });
  return updated;
}
